import { Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Features } from '@core/application/models/application';
import { AiService } from '@core/application/services/ai.service';
import { ApplicationService } from '@core/application/services/application.service';
import { FlutterService } from '@core/application/services/flutter.service';
import { NotificcationSignalingService } from '@core/application/signalling/notification-signalling.service';
import { TranslatePipe } from '@ngx-translate/core';
import { PrintPalletComponent } from '@shared/print/components/print-pallet.component';
import { PrintService } from '@shared/print/services/print.service';
import { ReleasenotesDialogComponent } from '@shared/releasenotes/dialogs/releasenotes-dialog.component';
import {
  BaseComponent,
  BranchService,
  BranchTabsComponent,
  BranchVerticalComponent,
  CultureService,
  GlobalMessageComponent,
  GlobalMessageService,
  HeaderComponent,
  ISession,
  IUiPreference,
  LoadingComponent,
  RouteService,
  SessionService,
} from '@simac/ui';
import { ShoppingCartButtonComponent } from '@webshop/shared/components/shopping-cart-button.component';
import { WebshopService } from '@webshop/shared/services/webshop.service';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ScrollTopModule } from '@openng/optimus-ui/scrolltop';
import { ToastModule } from '@openng/optimus-ui/toast';
import { SspGlobalMessgeService } from './features/serviceandsupport/shared/services/global-message.service';
import { AssistantChatWidgetComponent } from './features/serviceandsupport/assistant/components/assistant-chat-widget.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    HeaderComponent,
    GlobalMessageComponent,
    LoadingComponent,
    RouterOutlet,
    ScrollTopModule,
    ConfirmDialogModule,
    ToastModule,
    TranslatePipe,
    BranchTabsComponent,
    BranchVerticalComponent,
    NgxPermissionsModule,
    ShoppingCartButtonComponent,
    ReleasenotesDialogComponent,
    PrintPalletComponent,
    AssistantChatWidgetComponent,
  ],
})
export class AppComponent extends BaseComponent implements OnInit {
  readonly sessionService = inject(SessionService);
  readonly printService = inject(PrintService);
  readonly webshopService = inject(WebshopService);
  private readonly globalMessageService = inject(GlobalMessageService);
  private readonly applicationService = inject(ApplicationService);
  private readonly routeService = inject(RouteService);
  private readonly sspGlobalMessgeService = inject(SspGlobalMessgeService);
  private readonly aiService = inject(AiService);
  private readonly flutterService = inject(FlutterService);
  private readonly cultureService = inject(CultureService);
  private readonly branchService = inject(BranchService);
  private readonly notificcationSignalingService = inject(NotificcationSignalingService);
  private readonly permissionsService = inject(NgxPermissionsService);

  showSSPWebshop = false;
  verticalBranches = false;

  constructor() {
    super();
    this.translate.setFallbackLang('nl');
  }

  ngOnInit(): void {
    this.applicationService.registerModules();
    this.handleSubscriptions();
  }

  onReleaseNotes(): void {
    this.displayDialog = true;
  }

  onDialogHide(): void {
    this.displayDialog = false;
  }

  private handleSubscriptions(): void {
    this.routeService.subsribeOnRouting();
    this.sessionService.onSessionInitialized.subscribe(() => {
      // this.checkReleaseNotes();
      this.setBranchView();
      this.flutterService.onSessionInitialized();
      this.notificcationSignalingService.connectToNotificationHub();
    });
    this.sessionService.onSessionChanged.subscribe((session) => {
      this.sessionChanged(session);
      this.webshopService.checkAddons(session);
    });
    this.sessionService.onSessionExpired.subscribe(() => {
      this.flutterService.onSessionExpired();
      this.notificcationSignalingService.stopNotificationHubConnection();
    });
    this.aiService.setUpApplicationInsights();
  }

  private sessionChanged(session: ISession): void {
    this.setBranchContext(session);
    this.handleLanguage();
    this.globalMessageService.resetMessages();
    this.globalMessageService.checkGlobalMessages().subscribe();
    if (this.sessionService.hasFeatureEnabled(Features.ServiceAndSupport)) {
      this.sspGlobalMessgeService.getMajorIncident();
      if (this.permissionsService.getPermission('User.Profile.LimitedProfile') === undefined) {
        this.sspGlobalMessgeService.checkUserHasFullName(session);
      }
    }
  }

  private handleLanguage(): void {
    const culture = this.sessionService.session?.currentCulture ?? 'nl';
    this.cultureService.setTranslateCulture(culture);
  }

  private setBranchContext(session: ISession): void {
    const showPersonal = session.custom?.SspRequestInfo?.userExistsInTopdesk ?? false;
    this.branchService.setContext(showPersonal).subscribe();
  }

  private setBranchView(): void {
    if (window.matchMedia('(max-width: 992px)').matches) {
      this.verticalBranches = false;
    } else {
      this.apiService.get<IUiPreference>('api/runtime/session/uipreference/branchview').subscribe((result) => {
        this.verticalBranches = result.subjectValue === 'vertical';
        this.markForCheck();
      });
    }
  }
}
